import { memo, type RefObject } from "react";

import { Video } from "@shopify/hydrogen-react";

import { Icon } from "@components/Icon";
import { focusRing } from "@react/utilities";
import { type CollectionContentCard } from "@root/tablescape/state";
import { tw } from "classnames";

type Props = Pick<CollectionContentCard, "videoControlColor"> & {
  isVideoPlaying: boolean;
  onToggleVideo: () => void;
  videoRef: RefObject<HTMLVideoElement>;
  videoSources: {
    format: string;
    mimeType: string;
    url: string;
  }[];
};
const styles = {
  base: tw(
    "relative",
    "overflow-hidden",
    "before:float-left",
    "before:-ml-1",
    "before:w-1",
    "before:pt-full",
    "after:clear-both",
    "after:table",
    "md:before:h-0",
    "md:before:content-['']",
    "md:after:content-['']"
  ),
  videoContainer: tw("h-60", "w-full", "md:absolute", "md:h-full"),
  video: tw("w-full", "h-full", "object-cover"),
  videoButton: tw(
    "block",
    "border-0",
    "outline-none",
    "bg-transparent",
    "cursor-pointer",
    focusRing,
    "p-0",
    "absolute",
    "bottom-5",
    "left-5"
  ),
  videoIcon: ({ videoControlColor }: { videoControlColor?: string }) =>
    tw(
      {
        [tw(`text-char-50`)]: videoControlColor === "white",
        [tw(`text-primary`)]: videoControlColor === "char",
      },
      "w-10",
      "h-10",
      "lg:w-12",
      "lg:h-12"
    ),
};

export const CollectionVideoCardViewBase = ({
  isVideoPlaying,
  onToggleVideo,
  videoControlColor,
  videoRef,
  videoSources,
}: Props) => {
  return (
    <div className={styles.base}>
      <div className={styles.videoContainer}>
        {!!videoSources?.length && (
          <Video
            autoPlay
            className={styles.video}
            controls={false}
            data={{
              mediaContentType: "VIDEO",
              sources: videoSources,
            }}
            data-chromatic="ignore"
            loop
            muted
            playsInline
            ref={videoRef}
          />
        )}
      </div>
      <button className={styles.videoButton} onClick={onToggleVideo}>
        <Icon
          className={styles.videoIcon({ videoControlColor })}
          name={isVideoPlaying ? "pause" : "play-circle"}
        />
        <span className={tw("sr-only")}>
          {isVideoPlaying ? "pause" : "play"}
        </span>
      </button>
    </div>
  );
};

export const CollectionVideoCardView = memo(CollectionVideoCardViewBase);
